import React, { ComponentProps, ReactElement, useState } from 'react';
import DateField from '../connectedForm/DateInput/DateInput';
import CheckboxInput, { ICheckboxInputProps } from '../connectedForm/CheckboxInput/CheckboxInput';
import { ReportTemplate } from '../../graphql/entities';
import GenericReportModal from './GenericReportModal';
import Dropdown from '../connectedForm/Dropdown';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import {
  AccountBalanceCriteria,
  FilterOperation,
  IAgedCreditReportInput,
  IReportTemplatesDropdownQueryResult,
  IReportTemplatesDropdownQueryVariables,
  ReportOutputType,
  ReportType,
} from '../../graphql/schema/p4types';
import { useQuery } from '@apollo/client';
import { useActionProps } from '../../utils/useActionProps';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import GenerateAgedCreditReportMutation from '../../graphql/mutations/supplier/GenerateAgedCreditReportMutation.graphql';
import FluentModal from './FluentModal';

const GenericReportModalCheckboxInput = CheckboxInput as unknown as (
  props: Omit<ICheckboxInputProps, 'onChange' | 'value'> & { dataid: string }
) => ReactElement;

const GenericReportModalDateField = DateField as unknown as (
  props: Omit<ComponentProps<typeof DateField>, 'onChange' | 'value'> & { dataid: string }
) => ReactElement;

const AgedCreditCriteriaModal = ({
  isOpen,
  ...props
}: ComponentProps<typeof GenericReportModal> & { isOpen: boolean }) => {
  const [templates, setTemplates] = useState<Array<any>>([]);

  useQuery<IReportTemplatesDropdownQueryResult, IReportTemplatesDropdownQueryVariables>(ReportTemplate.dropdown.query, {
    variables: {
      filters: [
        {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'reportType',
            value: ReportType.AgedCreditReport,
          },
        },
        {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'reportType',
            value: ReportType.AgedCreditTransactionReport,
          },
        },
      ],
    },
    onCompleted: (data) => {
      setTemplates(data.list?.edges || []);
    },
  });

  const actionProps = useActionProps('suppliers');

  const getInput = (parentState: {
    isRetrospective: boolean;
    includeSuppliers: AccountBalanceCriteria;
    baseDate: any;
  }) => {
    if (actionProps.suppliers && !Array.isArray(actionProps.suppliers)) actionProps.suppliers = [];
    const input: IAgedCreditReportInput = {
      agedCreditReportCriteria: {
        findAllCriteria: {
          filters: actionProps.filters,
        },
        suppliers: normalizeVariables(actionProps.suppliers),
        include: actionProps.include,
        retrospective: parentState.isRetrospective,
        includeSuppliers: parentState.includeSuppliers,
      },
    };

    // date set?
    if (parentState.baseDate) {
      input.agedCreditReportCriteria!.baseDate = parentState.baseDate;
    }

    return input;
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '600px' } }}>
      <div style={{ padding: '1em' }}>
        <GenericReportModal
          templates={templates}
          input={getInput}
          title="Aged Creditor Report"
          outputSelection={[
            {
              value: ReportOutputType.Email,
              label: 'E-Mail',
            },
            {
              value: ReportOutputType.LocalPrinter,
              label: 'Local Printer',
            },
            {
              value: ReportOutputType.SystemPrinter,
              label: 'System Printer',
            },
            {
              value: ReportOutputType.Csv,
              label: 'CSV File',
            },
            {
              value: ReportOutputType.Xlsx,
              label: 'XLSX Spreadsheet',
            },
          ]}
          mutation={GenerateAgedCreditReportMutation}
          defaultState={{ includeSuppliers: AccountBalanceCriteria.All }}
          {...props}
        >
          <GenericReportModalDateField
            inputCols={8 / 12}
            labelCols={4 / 12}
            dataid="baseDate"
            label="Base date for ageing"
            portal={true}
            data-testid={'aged-debt-criteria-base-date'}
          />
          <GenericReportModalCheckboxInput
            inputCols={8 / 12}
            labelCols={4 / 12}
            dataid="isRetrospective"
            label="Retrospective"
            data-testid={'aged-debt-criteria-is-retrospective'}
          />
          <Dropdown
            options={fieldMetaData.AccountBalanceCriteria.enumValues}
            enum={true}
            valueKey="value"
            labelKey="label"
            label="Include suppliers with"
            rawLabel={true}
            inputCols={8 / 12}
            labelCols={4 / 12}
            dataid="includeSuppliers"
          />
        </GenericReportModal>
      </div>
    </FluentModal>
  );
};

export default AgedCreditCriteriaModal;
